"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import styles from "./range-slider.module.css";

interface RangeSliderProps {
  min: number;
  max: number;
  minValue: number;
  maxValue: number;
  onChange: (min: number, max: number) => void;
  step?: number;
  formatLabel?: (value: number) => string;
}

export default function RangeSlider({
  min,
  max,
  minValue,
  maxValue,
  onChange,
  step = 1,
  formatLabel = (v) => String(v),
}: RangeSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<"min" | "max" | null>(null);

  const clamp = (val: number, lo: number, hi: number) =>
    Math.max(lo, Math.min(hi, val));

  const snap = (val: number) => Math.round(val / step) * step;

  const pct = (val: number) => ((val - min) / (max - min)) * 100;

  const valFromX = useCallback(
    (clientX: number) => {
      const rect = trackRef.current!.getBoundingClientRect();
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
      return snap(min + ratio * (max - min));
    },
    [min, max, step],
  );

  const handlePointerDown = useCallback(
    (thumb: "min" | "max") => (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      setDragging(thumb);
    },
    [],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging) return;
      const v = valFromX(e.clientX);
      if (dragging === "min") {
        onChange(Math.min(v, maxValue - step), maxValue);
      } else {
        onChange(minValue, Math.max(v, minValue + step));
      }
    },
    [dragging, valFromX, minValue, maxValue, onChange, step],
  );

  const handlePointerUp = useCallback(() => {
    setDragging(null);
  }, []);

  // Track click to jump nearest thumb
  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      if (dragging) return;
      const v = valFromX(e.clientX);
      const distMin = Math.abs(v - minValue);
      const distMax = Math.abs(v - maxValue);
      if (distMin <= distMax) {
        onChange(Math.min(v, maxValue - step), maxValue);
      } else {
        onChange(minValue, Math.max(v, minValue + step));
      }
    },
    [dragging, valFromX, minValue, maxValue, onChange, step],
  );

  return (
    <div className={styles.rangeSlider}>
      <div className={styles.labels}>
        <span className={styles.labelValue}>{formatLabel(minValue)}</span>
        <span className={styles.labelSep}>—</span>
        <span className={styles.labelValue}>{formatLabel(maxValue)}</span>
      </div>
      <div
        ref={trackRef}
        className={styles.track}
        onClick={handleTrackClick}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <div className={styles.trackBg} />
        <div
          className={styles.trackFill}
          style={{
            left: `${pct(minValue)}%`,
            width: `${pct(maxValue) - pct(minValue)}%`,
          }}
        />
        <div
          className={`${styles.thumb} ${dragging === "min" ? styles.thumbActive : ""}`}
          style={{ left: `${pct(minValue)}%` }}
          onPointerDown={handlePointerDown("min")}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={maxValue}
          aria-valuenow={minValue}
          aria-label="Minimum value"
          tabIndex={0}
        />
        <div
          className={`${styles.thumb} ${dragging === "max" ? styles.thumbActive : ""}`}
          style={{ left: `${pct(maxValue)}%` }}
          onPointerDown={handlePointerDown("max")}
          role="slider"
          aria-valuemin={minValue}
          aria-valuemax={max}
          aria-valuenow={maxValue}
          aria-label="Maximum value"
          tabIndex={0}
        />
      </div>
      <div className={styles.bounds}>
        <span>{formatLabel(min)}</span>
        <span>{formatLabel(max)}</span>
      </div>
    </div>
  );
}
